import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import {
  FlowResourceType,
  flowSteps,
  flowsQuery,
  flowUtils,
  generateTestIds,
  useDialogs,
  useEditorStore,
  useEnvironment,
  useNotifications,
  useFlowBuilder2Store,
  useFlowBuilder2PublishFlowMutation,
  FlowByIdVersionEnum,
  isEmptyId,
  getResponseErrorMessage,
  Events,
  useAssistantLoadingStore,
  AdvancedEditorMarkerSeverity,
} from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { useValidate } from './useValidate';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';
import { FlowBuilder2PageViewNameEnum } from '../../Context/FlowBuilder2PageContext.types';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { isEqual } from 'lodash';
import {
  BuilderTypeEnum,
  FlowBuilderModalContentTypeEnum,
} from '../../Store/FlowBuilder2PageStore.types';
import { useSaveEditorBeforePublish } from '../../Support/useSaveEditorBeforePublish';

const PublishButtonTestIds = generateTestIds('PublishButton', ['button']);

export const PublishButton = (props: LoadingButtonProps) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const validate = useValidate();
  const { confirm } = useDialogs();
  const { error } = useNotifications();
  const environment = useEnvironment();
  const queryClient = useQueryClient();

  const flowId = useEditorStore((s) => s.flow?.id ?? '');
  const flowVersion = useEditorStore((s) => s.flow?.properties.dianaVersion?.versionNumber ?? 0);
  const flowResourceType = useEditorStore((s) => s.flow?.properties.flowResourceType);
  const getFlow = useEditorStore((s) => s.getFlow);
  const isSavingEditor = useEditorStore((s) => s.isSaving);
  const editorSelectedItemId = useEditorStore((s) => s.selectedItemId);
  const getEditorValidationMarkers = useEditorStore((s) => s.getValidationMarkers);

  const isRequesting = useFlowBuilder2Store((s) => !!s.requests.length);
  const setTaskCollapsed = useFlowBuilder2PageStore((s) => s.setTaskCollapsed);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const activeTab = useFlowBuilder2PageStore((store) => store.builderType);
  const setActiveTab = useFlowBuilder2PageStore((store) => store.setBuilderType);
  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const {
    isLoading: isPageLoading,
    error: pageError,
    setActiveView,
  } = useFlowBuilder2PageContext();

  const saveEditorBeforePublish = useSaveEditorBeforePublish();

  const { mutate, isPending: isPublishingFlow } = useFlowBuilder2PublishFlowMutation({
    onSuccess: (flow) => {
      clearFlowsCache({ flowId: flow.id });
      if (flowResourceType === FlowResourceType.Entity) {
        Events.OpenEntityGroup.post({ environment, flowOriginId: flow.properties.originId });
      } else {
        Events.OpenWorkboard.post({ environment, flowId: flow.id });
      }

      // update cache
      flowsQuery
        .getById({ id: flow.id, version: FlowByIdVersionEnum.Specific })
        .fn()
        .then(flowUtils.reflectPublishedFlowToCache)
        .catch(console.error);
    },
    onError: (axiosError) => {
      error({
        message: t('errors.publishFlowFailed'),
        description: getResponseErrorMessage(axiosError),
      });
    },
  });

  const publishFlow = async () => {
    if (hasAnyPendingStatus) {
      return setActiveModal({ name: FlowBuilderModalContentTypeEnum.AiAcceptReject, context: {} });
    }

    if (activeTab === BuilderTypeEnum.AdvancedEditor) {
      const markers = getEditorValidationMarkers(
        editorSelectedItemId,
        AdvancedEditorMarkerSeverity.Error,
      );
      if (markers.length) {
        error({
          message: t('titles.actionRequired'),
          description: t('descriptions.resolveIssues'),
        });
        setActiveView(FlowBuilder2PageViewNameEnum.Builder);
        return;
      }
    }

    await saveEditorBeforePublish();

    const validationResult = validate();
    if (validationResult) {
      error({ message: t('titles.actionRequired'), description: t('descriptions.resolveIssues') });
      setActiveTab(BuilderTypeEnum.Nocode);
      setActiveView(FlowBuilder2PageViewNameEnum.Builder);

      const flow = getFlow();
      if (flow) {
        for (const step of flow.steps) {
          if (!isEmptyId(step.taskId)) {
            setTaskCollapsed(step.taskId, false);
          }
        }
        setTimeout(() => {
          const errorElement = document.querySelector(
            '.Mui-error input, .Mui-error textarea, [data-testid="ErrorIcon"]',
          );
          if (errorElement)
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });
        }, 200);
      }

      return;
    }

    if (flowVersion > 0) {
      const { confirmed } = await confirm(null, {
        title: t('prompts.areYouSureToPublish', {
          flowTypeName:
            flowResourceType === FlowResourceType.Entity
              ? t('labels.assetType').toLowerCase()
              : t('labels.workflow').toLowerCase(),
        }),
        variant: 'success',
        confirmButtonLabel: t('buttons.publish'),
      });
      if (!confirmed) return;
    }

    mutate({ flowId });
  };

  const clearFlowsCache = (args: { flowId: string }) => {
    const { flowId } = args;
    const stepsQueryKey = flowSteps.getById({ id: flowId }).key;
    queryClient.removeQueries({ queryKey: stepsQueryKey });
  };

  if (isPageLoading) return <Skeleton variant="rounded" width={72} height={32} />;

  if (!pageError)
    return (
      <LoadingButton
        {...props}
        style={{ padding: '6px 8px', minHeight: 0 }}
        variant="contained"
        disabled={isRequesting || isChatLoading || isSavingEditor}
        loading={isPublishingFlow}
        onClick={publishFlow}
        data-testid={PublishButtonTestIds.button}
      >
        {t('buttons.publish')}
      </LoadingButton>
    );

  return null;
};
